<script setup lang="ts">
import { computed, watch } from 'vue'
import AppHeader from './AppHeader.vue'
import AppIconRail from './AppIconRail.vue'
import AppMobileNav from './AppMobileNav.vue'
import AppNotifications from '@/components/common/AppNotifications.vue'
import AiChatWrapper from '@/modules/ai/components/AiChatWrapper.vue'
import NotificationSlideOver from '@/modules/notifications/components/NotificationSlideOver.vue'
import AppModal from '@/components/common/AppModal.vue'
import { useAppStore } from '@/core/stores/useAppStore'
import { useProjectsStore } from '@/modules/projects/stores/useProjectsStore'
import { useAuthStore } from '@/modules/auth/stores/useAuthStore'
import { logger } from '@/core/utils/logger'

const appStore = useAppStore()
const projectsStore = useProjectsStore()
const authStore = useAuthStore()
const isMobileMenuOpen = computed(() => appStore.isMobileMenuOpen)

// Load projects when auth is ready (after token refresh on page reload)
const loadProjects = async () => {
  if (authStore.isAuthenticated && authStore.isAuthReady) {
    try {
      await projectsStore.fetchAll()
      await projectsStore.initDefaultProject()
    } catch (error: unknown) {
      logger.error('Chyba při načítání projektů:', error)
    }
  }
}

// Watch for auth ready state
watch(() => authStore.isAuthReady, (isReady) => {
  if (isReady && authStore.isAuthenticated) {
    loadProjects()
  }
}, { immediate: true })
</script>

<template>
  <div class="flex h-screen w-full overflow-hidden bg-base">
    <!-- Mobile overlay -->
    <div
      v-if="isMobileMenuOpen"
      class="fixed inset-0 bg-black/50 z-40 md:hidden"
      @click="appStore.toggleMobileMenu()"
    ></div>

    <!-- Icon Rail (desktop only) -->
    <div class="hidden md:flex">
      <AppIconRail />
    </div>

    <!-- Mobile navigation drawer -->
    <div
      class="fixed inset-y-0 left-0 z-50 transform transition-transform duration-200 md:hidden"
      :class="isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'"
    >
      <AppMobileNav />
    </div>

    <!-- Main content area -->
    <div class="flex-1 flex flex-col min-w-0 overflow-hidden">
      <AppHeader />
      <main class="flex-1 overflow-y-auto p-0 sm:p-4">
        <slot />
      </main>
    </div>

    <AppNotifications />
    <AiChatWrapper />
    <NotificationSlideOver />

    <!-- Global modal -->
    <AppModal
      v-if="appStore.confirmModalConfig"
      :title="appStore.confirmModalConfig?.title || ''"
      :message="appStore.confirmModalConfig?.message || ''"
      :confirm-text="appStore.confirmModalConfig?.confirmText"
      :cancel-text="appStore.confirmModalConfig?.cancelText"
      :confirm-type="appStore.confirmModalConfig?.confirmType"
      :type="appStore.confirmModalConfig?.type"
      :is-open="appStore.isConfirmModalOpen"
      @confirm="appStore.handleConfirmAction"
      @cancel="appStore.handleCancelAction"
    />
  </div>
</template>
